import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { activityFlatsOfModels } from '@components/pages/activity-log/services/activity-log-flat.util';
import { EmptyStateComponent } from '@components/shared/empty-state/empty-state.component';
import { ErrorStateComponent } from '@components/shared/error-state/error-state.component';
import { BookingChain } from '@services/api/booking/booking-chain.model';
import { BookingChainErrorMapper } from '@services/api/booking/booking-chain-error.mapper';
import { BookingChainService } from '@services/api/booking/booking-chain.service';
import { format } from 'date-fns';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { HostActivityLogService } from '../../../shared/host-activity-log.service';
import { chainMetadata } from '../../../shared/host-activity.metadata';
import { BookingChainLinkService, ChainCandidate } from '../../services/booking-chain-link.service';

@Component({
  selector: 'app-booking-chain-link-dialog',
  standalone: true,
  imports: [
    DatePipe,
    NgTemplateOutlet,
    TranslateModule,
    DialogModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    SkeletonModule,
    PrimeTemplate,
    EmptyStateComponent,
    ErrorStateComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './booking-chain-link-dialog.component.html'
})
export class BookingChainLinkDialogComponent {
  readonly visible = model<boolean>(false);
  readonly chain = input.required<BookingChain>();
  readonly linked = output<void>();

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly loadFailed = signal(false);
  readonly candidates = signal<ChainCandidate[]>([]);
  readonly search = signal('');
  readonly othersExpanded = signal(false);
  readonly selectedId = signal<number | null>(null);

  private readonly linkService = inject(BookingChainLinkService);
  private readonly chainService = inject(BookingChainService);
  private readonly errorMapper = inject(BookingChainErrorMapper);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly hostActivityLog = inject(HostActivityLogService);

  readonly tail = computed(() => this.chainService.tailOf(this.chain()));
  readonly filtered = computed(() => this.applySearch(this.candidates(), this.search()));
  readonly sameFlat = computed(() => this.filtered().filter((candidate) => candidate.sameFlat));
  readonly otherFlat = computed(() => this.filtered().filter((candidate) => !candidate.sameFlat));
  readonly selected = computed(() => this.filtered().find((candidate) => candidate.id === this.selectedId()) ?? null);

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.loadCandidates();
      }
    });
  }

  onSelect(candidate: ChainCandidate): void {
    if (candidate.alreadyChained) {
      return;
    }
    this.selectedId.set(candidate.id);
  }

  onConfirm(): void {
    const candidate = this.selected();
    if (!candidate) {
      return;
    }
    this.saving.set(true);
    const linked = chainMetadata({ bookingId: candidate.id, flats: activityFlatsOfModels(candidate.booking.flats), previousId: this.tail().id });
    this.linkService.link(candidate.id, this.tail().iri).subscribe({
      next: () => {
        this.hostActivityLog.log('chain_link', linked);
        this.onLinkSuccess();
      },
      error: (error: unknown) => this.onLinkError(error)
    });
  }

  onCancel(): void {
    this.visible.set(false);
  }

  private loadCandidates(): void {
    this.loading.set(true);
    this.loadFailed.set(false);
    this.selectedId.set(null);
    this.search.set('');
    this.linkService.loadCandidates(this.chain()).subscribe({
      next: (candidates) => this.onCandidatesLoaded(candidates),
      error: () => this.onCandidatesLoadError()
    });
  }

  private onCandidatesLoaded(candidates: ChainCandidate[]): void {
    this.candidates.set(candidates);
    this.othersExpanded.set(!candidates.some((candidate) => candidate.sameFlat));
    this.loading.set(false);
  }

  private onCandidatesLoadError(): void {
    this.candidates.set([]);
    this.loadFailed.set(true);
    this.loading.set(false);
  }

  private onLinkSuccess(): void {
    this.saving.set(false);
    this.visible.set(false);
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.success'),
      detail: this.translateService.instant('booking.chain.success.linked'),
      life: 3000
    });
    this.linked.emit();
  }

  private onLinkError(error: unknown): void {
    this.saving.set(false);
    const mapped = this.errorMapper.map(error);
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.error'),
      detail: [this.translateService.instant(mapped.messageKey), mapped.details].filter(Boolean).join(' '),
      life: 6000
    });
    if (mapped.kind === 'alreadyChained') {
      this.loadCandidates();
    }
  }

  private applySearch(candidates: ChainCandidate[], term: string): ChainCandidate[] {
    const needle = term.trim().toLowerCase();
    if (!needle) {
      return candidates;
    }
    return candidates.filter((candidate) =>
      `${candidate.guestName} ${candidate.id} ${candidate.flatLabel ?? ''}`.toLowerCase().includes(needle)
    );
  }

  previewEnd(candidate: ChainCandidate): string {
    return format(candidate.endDate, 'yyyy-MM-dd');
  }

  previewNights(candidate: ChainCandidate): number {
    return this.chain().totalNights + candidate.nights;
  }
}
